"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ButtonGroup = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const Icon_1 = require("./Icon");
const Tooltip_1 = require("./Tooltip");
const Css_1 = require("../Css");
const utils_1 = require("../utils");
const defaultTestId_1 = require("../utils/defaultTestId");
function ButtonGroup(props) {
    const { buttons, disabled = false, size = "sm" } = props;
    const tid = (0, utils_1.useTestIds)(props, "buttonGroup");
    return (
    // Adding `line-height: 0` prevent inheriting line-heights that might throw off sizing within the button group.
    (0, jsx_runtime_1.jsx)("div", { ...tid, css: Css_1.Css.df.lh(0).add({ ...sizeStyles[size] }).$, children: buttons.map(({ disabled: buttonDisabled, ...buttonProps }, i) => (
        // Disable the button if the ButtonGroup is disabled or if the current button is disabled.
        (0, jsx_runtime_1.jsx)(GroupButton, { ...buttonProps, disabled: disabled || buttonDisabled, size: size, ...tid }, i))) }));
}
exports.ButtonGroup = ButtonGroup;
function GroupButton(props) {
    var _a;
    const { icon, text, active, onClick: onPress, disabled, size, tooltip, ...otherProps } = props;
    const ariaProps = { onPress, isDisabled: !!disabled, ...otherProps };
    const ref = (0, react_1.useRef)(null);
    const { buttonProps, isPressed } = (0, react_aria_1.useButton)(ariaProps, ref);
    const { isFocusVisible, focusProps } = (0, react_aria_1.useFocusRing)();
    const { hoverProps, isHovered } = (0, react_aria_1.useHover)(ariaProps);
    const tid = (0, utils_1.useTestIds)(props);
    return ((0, jsx_runtime_1.jsx)("span", { css: getButtonStyles(), children: (0, Tooltip_1.maybeTooltip)({
            title: (0, Tooltip_1.resolveTooltip)(disabled, tooltip),
            placement: "top",
            children: ((0, jsx_runtime_1.jsxs)("button", { ref: ref, ...buttonProps, ...focusProps, ...hoverProps, css: {
                    ...Css_1.Css.buttonBase.px2.br0.h100.$,
                    "&:disabled": Css_1.Css.gray400.cursorNotAllowed.bGray300.$,
                    ...(isFocusVisible ? defaultFocusRingStyles : {}),
                    ...(active ? activeStyles : {}),
                    ...(isPressed ? pressedStyles : isHovered ? hoverStyles : {}),
                    ...(icon ? iconStyles[size] : {}),
                }, ...tid[(0, defaultTestId_1.defaultTestId)((_a = text !== null && text !== void 0 ? text : icon) !== null && _a !== void 0 ? _a : "button")], children: [icon && (0, jsx_runtime_1.jsx)(Icon_1.Icon, { icon: icon }), text] })),
        }) }));
}
const pressedStyles = Css_1.Css.bgGray200.$;
const activeStyles = Css_1.Css.bgGray300.$;
const hoverStyles = Css_1.Css.bgGray100.$;
const defaultFocusRingStyles = Css_1.Css.relative.z2.bshFocus.$;
function getButtonStyles() {
    return {
        ...Css_1.Css.z1.bgWhite.bGray300.bw1.ba.gray900.br0.overflowHidden.$,
        // Our first button should have a rounded left border
        "&:first-of-type": Css_1.Css.add("borderRadius", "4px 0 0 4px").$,
        // Our last button should have a rounded right border
        "&:last-of-type": Css_1.Css.add("borderRadius", "0 4px 4px 0").$,
        // Nudge buttons one pixel to the left so they visually share a border
        "&:not(:first-of-type)": Css_1.Css.mlPx(-1).$,
    };
}
const sizeStyles = {
    xs: Css_1.Css.hPx(28).$,
    sm: Css_1.Css.hPx(32).$,
    md: Css_1.Css.hPx(40).$,
};
const iconStyles = {
    xs: Css_1.Css.pxPx(2).$,
    sm: Css_1.Css.pxPx(4).$,
    md: Css_1.Css.px1.$,
};
